'use client'

import React, { useEffect, useState } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import {
  BookOpen,
  Feather,
  GraduationCap,
  Users,
  Search,
  LogIn,
  LogOut,
  ShieldCheck,
  Menu,
  X,
} from 'lucide-react'
import type { VisitorAccessContext } from '@/services/auth'

export function SiteHeader({ visitor }: { visitor: VisitorAccessContext }) {
  const pathname = usePathname()
  const [mobileOpen, setMobileOpen] = useState(false)

  useEffect(() => {
    setMobileOpen(false)
  }, [pathname])

  const navItems = [
    { href: '/library', label: 'Library', icon: BookOpen },
    { href: '/blog', label: 'Blog', icon: Feather },
    { href: '/contributors', label: 'Contributors', icon: Users },
    { href: '/profs-getting-started', label: 'Professors Page', icon: GraduationCap },
  ]

  return (
    <header className="wl-header">
      <input
        type="checkbox"
        id="wl-mobile-menu-toggle"
        className="wl-mobile-checkbox"
        checked={mobileOpen}
        onChange={(e) => setMobileOpen(e.target.checked)}
        aria-hidden="true"
      />

      <div className="wl-header-inner">
        <Link href="/" className="wl-brand" onClick={() => setMobileOpen(false)}>
          <div className="wl-brand-mark" aria-hidden="true">
            W
          </div>
          <div className="wl-brand-text">
            <span className="wl-brand-title">Worship Library</span>
            <span className="wl-brand-subtitle">The Complete Library of Christian Worship</span>
          </div>
        </Link>

        <nav className="wl-nav-desktop" aria-label="Main Navigation">
          {navItems.map((item) => {
            const isActive =
              pathname === item.href || (item.href !== '/' && pathname?.startsWith(item.href))
            return (
              <Link
                key={item.href}
                href={item.href}
                className={`wl-nav-link ${isActive ? 'active' : ''}`}
              >
                <span>{item.label}</span>
              </Link>
            )
          })}
        </nav>

        <div className="wl-header-actions">
          {visitor.isAuthenticated ? (
            <a
              href={`/api/wlib-auth/logout?mode=simulate-guest&returnTo=${encodeURIComponent(pathname || '/')}`}
              className="wl-btn wl-btn-outline wl-btn-sm"
              title="Log out"
            >
              <LogOut size={14} />
              <span>Logout</span>
            </a>
          ) : (
            <>
              <Link href="/login" className="wl-btn wl-btn-outline wl-btn-sm">
                <span>Login</span>
              </Link>
              <Link href="/subscribe" className="wl-btn wl-btn-primary wl-btn-sm">
                <span>Sign Up</span>
              </Link>
            </>
          )}
        </div>

        <label
          htmlFor="wl-mobile-menu-toggle"
          className="wl-mobile-toggle"
          aria-label="Toggle Menu"
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              setMobileOpen((prev) => !prev)
            }
          }}
        >
          <span className="wl-icon-menu">
            <Menu size={22} />
          </span>
          <span className="wl-icon-close">
            <X size={22} />
          </span>
        </label>
      </div>

      {/* Mobile Drawer */}
      <div className={`wl-mobile-drawer ${mobileOpen ? 'open' : ''}`}>
        <div className="wl-mobile-nav-list">
          {navItems.map((item) => {
            const Icon = item.icon
            const isActive =
              pathname === item.href || (item.href !== '/' && pathname?.startsWith(item.href))
            return (
              <Link
                key={item.href}
                href={item.href}
                className={`wl-nav-link ${isActive ? 'active' : ''}`}
                onClick={() => setMobileOpen(false)}
              >
                <Icon size={16} />
                <span>{item.label}</span>
              </Link>
            )
          })}

          <Link
            href="/search"
            className={`wl-nav-link ${pathname?.startsWith('/search') ? 'active' : ''}`}
            onClick={() => setMobileOpen(false)}
          >
            <Search size={16} />
            <span>Search the Library</span>
          </Link>

          <hr
            style={{
              border: 0,
              borderTop: '1px solid var(--border-subtle)',
              margin: '0.5rem 0',
            }}
          />

          <div className="wl-mobile-actions">
            {visitor.isAuthenticated ? (
              <>
                <div className="wl-mobile-auth-badge">
                  <ShieldCheck size={15} />
                  <span>
                    {visitor.organization
                      ? visitor.organization.name
                      : visitor.user?.email || 'Logged In'}
                  </span>
                </div>
                <a
                  href={`/api/wlib-auth/logout?mode=simulate-guest&returnTo=${encodeURIComponent(pathname || '/')}`}
                  className="wl-btn wl-btn-outline wl-btn-sm"
                  style={{ width: '100%' }}
                >
                  <LogOut size={14} />
                  <span>Logout</span>
                </a>
              </>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                <Link
                  href="/login"
                  className="wl-btn wl-btn-outline"
                  onClick={() => setMobileOpen(false)}
                >
                  <span>Login</span>
                </Link>
                <Link
                  href="/subscribe"
                  className="wl-btn wl-btn-primary"
                  onClick={() => setMobileOpen(false)}
                >
                  <span>Sign Up</span>
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  )
}
